import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '~/common/utils/styleUtil';
import { sanitizeDigits } from '~/common/utils/stringUtil';
import { FileStack, LucideLoaderCircle, LucideMinus, LucidePlus, LucideShoppingCart, LucideCheck } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '~/common/components/ui/popover';
import { Separator } from '~/common/components/ui/separator';
import { LoginButton } from '~/shell/components/LoginButton';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/common/components/ui/tooltip';
import { AddArticleToTemplateButton } from '~/features/templates/routes/resources/AddArticleToTemplateRoute';
import { AddArticleToInventoryButton } from '~/features/inventory/routes/resources/AddArticleToInventoryRoute';
import { ProfitPlusDialog } from '~/common/components/profitPlus/ProfitPlusDialog';
import { getArticleQuantityCache, setArticleQuantityCache } from '~/common/articleQuantityStorageClient.client.ts';
import { Media } from '~/media.ts';
import { useCartIsBusy } from '../../cart/state-hooks/useCartIsBusy';
import { useIsArticleInCart } from '../../cart/state-hooks/useIsArticleInCart';
import { useAddArticleToCart } from '../../cart/state-hooks/useAddArticleToCart';

const checkQuantity = (value: string, allowZero?: boolean) => {
  let number = Number(value);
  if (value === '' || (number === 0 && !allowZero)) {
    number = allowZero ? 0 : 1;
  }
  const isValid = !Number.isNaN(number) && (allowZero ? number >= 0 : number > 0) && number <= 1000;
  return { isValid, number };
};

const disabledStyles = cn(
  'text-neutral-400 border-neutral-200 hover:bg-white bg-white desktop:bg-white, desktop:text-neutral-400 desktop:hover:bg-white'
);

function UnauthenticatedLoginButtons({ showLargeButtonOnDesktop }: Readonly<{ showLargeButtonOnDesktop: boolean }>) {
  return (
    <>
      <Media lessThan={'desktop'} className={'flex-1'}>
        <LoginButton variant={'default'} size={'sm'} className={cn('w-full')} />
      </Media>
      <Media greaterThanOrEqual={'desktop'} className={'flex-1'}>
        <LoginButton variant={'default'} size={showLargeButtonOnDesktop ? 'lg' : 'sm'} className={cn('w-full')} />
      </Media>
    </>
  );
}

function CartSubmitButton({
  showTooltip,
  onTooltipOpenChange,
  isValidNumber,
  isProcessing,
  isAddingPosition,
  isInCart,
  hideCart,
  showLargeButtonOnDesktop,
  articleNumber,
  calculatedTotalPriceText
}: Readonly<{
  showTooltip: boolean;
  onTooltipOpenChange: (open: boolean) => void;
  isValidNumber: boolean;
  isProcessing: boolean;
  isAddingPosition: boolean;
  isInCart: boolean;
  hideCart?: boolean;
  showLargeButtonOnDesktop: boolean;
  articleNumber: string;
  calculatedTotalPriceText: string;
}>) {
  const { t } = useTranslation();

  return (
    <Tooltip open={showTooltip && isValidNumber} onOpenChange={onTooltipOpenChange}>
      <TooltipTrigger asChild disabled={!isValidNumber}>
        <button
          type={'submit'}
          disabled={!isValidNumber || isProcessing || hideCart}
          className={cn(
            'relative border rounded items-center justify-center flex w-9 h-9 text-primary border-primary bg-white hover:bg-primary-light',
            showLargeButtonOnDesktop &&
              'desktop:bg-primary desktop:px-2 desktop:w-auto desktop:h-11 desktop:text-white desktop:hover:bg-primary/90',
            isInCart && 'bg-primary text-white hover:bg-primary/90',
            (!isValidNumber || isProcessing || hideCart) && disabledStyles
          )}
          onClick={e => e.stopPropagation()}
          data-testid={`addtocart-button-${articleNumber}`}
        >
          <span className={'contents'}>
            {isInCart && (
              <LucideCheck
                className={cn(
                  'absolute top-0 right-0 translate-x-1/2 -translate-y-1/4 text-primary bg-white rounded-full p-1 z-10 ring-1 ring-primary w-[19px] h-[19px]',
                  showLargeButtonOnDesktop && 'desktop:w-[24px] desktop:h-[24px]'
                )}
              />
            )}
          </span>
          <span className={'contents'}>
            {isAddingPosition ? (
              <LucideLoaderCircle
                className={cn(
                  'animate-spin w-[14px] h-[14px]',
                  showLargeButtonOnDesktop && 'desktop:w-[18px] desktop:h-[18px]'
                )}
              />
            ) : (
              <LucideShoppingCart
                className={cn('w-[14px] h-[14px]', showLargeButtonOnDesktop && 'desktop:w-[18px] desktop:h-[18px]')}
              />
            )}
          </span>
          <div className={cn('ml-2 hidden', showLargeButtonOnDesktop && 'desktop:block')}>
            <span>{t('In den Warenkorb')}</span>
          </div>
        </button>
      </TooltipTrigger>
      <TooltipContent className="z-[49]">
        <p className={'font-bold'} data-testid={`addtocart-price-tooltip-${articleNumber}`}>
          {calculatedTotalPriceText}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

function OtherActionsPopover({
  open,
  onOpenChange,
  showLargeButtonOnDesktop,
  articleNumber,
  articleDescriptionText,
  mainArticleId,
  isExternalArticle,
  quantity,
  hideAddToTemplate
}: Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showLargeButtonOnDesktop: boolean;
  articleNumber: string;
  articleDescriptionText: string;
  mainArticleId: number;
  isExternalArticle: boolean;
  quantity: number;
  hideAddToTemplate: boolean;
}>) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger
        asChild
        //                              disabled={hideCart}
      >
        <button
          type="button"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onOpenChange(true);
          }}
          className={cn(
            'border border-neutral-200 hover:bg-primary-light hover:text-primary hover:border-primary ' +
              'text-secondary bg-neutral-100 rounded-full flex items-center justify-center w-9 h-9',
            showLargeButtonOnDesktop && 'desktop:w-11 desktop:h-11'
          )}
          data-testid={`article-actions-button-${articleNumber}`}
        >
          <FileStack size={18} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-fit flex flex-col justify-start p-0 z-40" onClick={e => e.stopPropagation()}>
        {!hideAddToTemplate && (
          <>
            <AddArticleToTemplateButton
              articles={[
                {
                  mainArticleId,
                  isExternalArticle,
                  quantity,
                  articleNumber,
                  articleDescriptionText
                }
              ]}
              isAgh={false}
              onClose={() => onOpenChange(false)}
              forceSize={'large'}
            />
            <Separator />
          </>
        )}
        <AddArticleToInventoryButton
          articles={[{ mainArticleId, isExternalArticle, quantity }]}
          onClose={() => onOpenChange(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

export interface AddToCartProps {
  articleNumber: string;
  articleDescriptionText: string;
  mainArticleId: number;
  isExternalArticle: boolean;
  isProfitPlus?: boolean;
  calculatedTotalPriceText: string;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  allowZero?: boolean;
  isAuthenticated: boolean;
  hideCart?: boolean;
  className?: string;
  loadArticleQuantityCache?: boolean;
  writeArticleQuantityCache?: boolean;
  hideTemplateButton?: boolean;
  disableAmountInput?: boolean;
  hideAddToTemplate: boolean;
  showLargeButtonOnDesktop?: boolean;
}

export function AddToCart({
  articleNumber,
  articleDescriptionText,
  mainArticleId,
  isExternalArticle,
  isProfitPlus,
  calculatedTotalPriceText,
  quantity,
  onQuantityChange,
  allowZero,
  isAuthenticated,
  hideCart,
  className,
  hideTemplateButton = false,
  loadArticleQuantityCache = false,
  writeArticleQuantityCache = false,
  disableAmountInput = false,
  hideAddToTemplate,
  showLargeButtonOnDesktop = false
}: Readonly<AddToCartProps>) {
  const [internalQuantityState, setInternalQuantityState] = useState<string>(quantity.toString());
  const [showTooltip, setShowTooltip] = useState(false);
  const [otherActionsOpen, setOtherActionsOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { addArticleToCart, isAddingPosition } = useAddArticleToCart(
    isExternalArticle ? mainArticleId.toString() : articleNumber,
    isExternalArticle,
    articleDescriptionText
  );
  const isInCart = useIsArticleInCart(articleNumber, isExternalArticle);
  const { cartIsRefreshing } = useCartIsBusy();

  const setInternalQuantity = useCallback(
    (value: string) => {
      setInternalQuantityState(value);

      if (writeArticleQuantityCache) {
        setArticleQuantityCache(articleNumber, +value);
      }
    },
    [articleNumber, writeArticleQuantityCache]
  );

  const increment = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const newQuantity = quantity + 1;
    setInternalQuantity(newQuantity.toString());
    onQuantityChange(newQuantity);
  };

  const decrement = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const newQuantity = quantity - 1;
    setInternalQuantity(newQuantity.toString());
    onQuantityChange(newQuantity);
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();

    const value = sanitizeDigits(event.target.value);

    if (value === '') {
      setInternalQuantityState('');
      onQuantityChange(allowZero ? 0 : 1);
      return;
    }

    const { number, isValid } = checkQuantity(value, true);

    if (isValid) {
      setInternalQuantity(number.toString());
      onQuantityChange(number);
    }
  };

  useEffect(() => {
    const loadQuantity = async () => {
      const cachedQuantity = await getArticleQuantityCache(articleNumber);
      const { number, isValid } = checkQuantity(cachedQuantity.toString(), true);

      if (isValid && number?.toString() !== internalQuantityState) {
        setInternalQuantity(number.toString());
        onQuantityChange(number);
      }
    };

    if (loadArticleQuantityCache) {
      loadQuantity();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const canDecrement = quantity >= 1 && !isAddingPosition && !disableAmountInput;
  const canIncrement = quantity < 1000 && !isAddingPosition && !disableAmountInput;
  const { isValid: isValidNumber } = checkQuantity(internalQuantityState, allowZero);
  const isProcessing = isAddingPosition || cartIsRefreshing;

  function submitAddPosition(e: React.SubmitEvent<HTMLFormElement>) {
    e.preventDefault();
    e.stopPropagation();

    if (isProfitPlus) {
      setIsOpen(true);
    } else {
      addArticleToCart(quantity);
    }
  }

  function handleClose() {
    setIsOpen(false);
  }

  function handleConfirm() {
    setIsOpen(false);
    addArticleToCart(quantity);
  }

  const handleOpenChange = (open: boolean) => {
    setOtherActionsOpen(open);
  };

  useEffect(() => {
    if (quantity?.toString() !== internalQuantityState && internalQuantityState !== '') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInternalQuantity(quantity.toString());
    }
  }, [internalQuantityState, quantity, setInternalQuantity]);

  return (
    <div className={cn('flex justify-between items-center min-w-[200px] w-full', className)}>
      {!isAuthenticated && <UnauthenticatedLoginButtons showLargeButtonOnDesktop={showLargeButtonOnDesktop} />}
      {isAuthenticated && (
        <div className={cn('flex gap-2')}>
          <form onSubmit={e => submitAddPosition(e)} className={cn('flex gap-2 items-center')}>
            <input name={'articleNumber'} type={'hidden'} value={articleNumber} />
            <div
              className={cn(
                'flex items-center border rounded max-w-[7rem] h-9',
                showLargeButtonOnDesktop && 'desktop:max-w-[9rem] desktop:h-11'
              )}
            >
              <button
                type="button"
                id="decrement-button"
                onClick={decrement}
                disabled={!canDecrement}
                className={cn(
                  'bg-white text-primary hover:bg-primary-light h-full border-r rounded-l p-2',
                  showLargeButtonOnDesktop && 'desktop:p-4 desktop:aspect-square',
                  !canDecrement && disabledStyles,
                  !isValidNumber && disabledStyles
                )}
                data-testid={`addtocart-decrement-button${articleNumber}`}
              >
                <LucideMinus size={14} />
              </button>
              <input
                type="text"
                id={`quantity-input-${articleNumber}`}
                value={internalQuantityState}
                name={'quantity'}
                onChange={handleAmountChange}
                className="font-bold text text-center block w-full outline-hidden"
                data-testid={`addtocart-amount-input-${articleNumber}`}
                required
                disabled={isAddingPosition || disableAmountInput}
                onBlur={() => {
                  // Schreibt den Wert beim Verlassen des Feldes zurück, falls leer
                  if (internalQuantityState === '') {
                    setInternalQuantity(quantity.toString());
                  }
                }}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onFocus={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={e => {
                  e.stopPropagation();
                }}
                onDoubleClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
              <button
                type="button"
                id="increment-button"
                disabled={!canIncrement}
                onClick={increment}
                className={cn(
                  'bg-white text-primary hover:bg-primary-light h-full border-l rounded-r p-2',
                  showLargeButtonOnDesktop && 'desktop:p-4 desktop:aspect-square',
                  !canIncrement && disabledStyles,
                  !isValidNumber && internalQuantityState !== '0' && disabledStyles
                )}
                data-testid={`addtocart-increment-button-${articleNumber}`}
              >
                <LucidePlus size={14} />
              </button>
            </div>
            <CartSubmitButton
              showTooltip={showTooltip}
              onTooltipOpenChange={setShowTooltip}
              isValidNumber={isValidNumber}
              isProcessing={isProcessing}
              isAddingPosition={isAddingPosition}
              isInCart={isInCart}
              hideCart={hideCart}
              showLargeButtonOnDesktop={showLargeButtonOnDesktop}
              articleNumber={articleNumber}
              calculatedTotalPriceText={calculatedTotalPriceText}
            />
          </form>

          {isProfitPlus && isOpen && (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            <div
              className={'flex flex-row'}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <ProfitPlusDialog
                articleNumber={articleNumber}
                useTrigger={false}
                onClose={handleClose}
                onConfirm={handleConfirm}
                showFooter={true}
              />
            </div>
          )}
          {!hideTemplateButton && (
            <OtherActionsPopover
              open={otherActionsOpen}
              onOpenChange={handleOpenChange}
              showLargeButtonOnDesktop={showLargeButtonOnDesktop}
              articleNumber={articleNumber}
              articleDescriptionText={articleDescriptionText}
              mainArticleId={mainArticleId}
              isExternalArticle={isExternalArticle}
              quantity={quantity}
              hideAddToTemplate={hideAddToTemplate}
            />
          )}
        </div>
      )}
    </div>
  );
}
